/**
 * Thread lifecycle rows — the client half (chat-thread experience PR-5,
 * 2026-09-09). The server records every change TO a shared thread IN the
 * thread (functions/src/prest0nSession/threadLifecycle.ts): a rename, a
 * person added / removed / leaving, Prest0n taken out or put back. The doc
 * arrives through the ordinary group overlay as a message with
 * `sourceTag:'system:lifecycle'` and the facts (`lifecycleType`, `actorId`,
 * `actorName`, `detail`); the transcript renders it as a centered system row
 * in the VIEWER's language ("you" when the viewer did it) — chrome copy
 * authored here (D9), never by the model.
 */
import type { ChatMessage } from '../../components/GlobalChatSystem/types';

export const LIFECYCLE_SOURCE_TAG = 'system:lifecycle';

export type ThreadLifecycleType =
  | 'renamed'
  | 'member-added'
  | 'member-removed'
  | 'member-left'
  | 'assistant-removed'
  | 'assistant-added';

export interface ThreadLifecycleFacts {
  type: ThreadLifecycleType;
  actorId: string;
  actorName: string;
  detail: { title?: string; previousTitle?: string; targetId?: string; targetName?: string };
}

const trim = (v: unknown): string => (typeof v === 'string' ? v.trim() : '');
const quote = (v: string): string => `“${v}”`;

const KNOWN_TYPES: ReadonlySet<string> = new Set([
  'renamed', 'member-added', 'member-removed', 'member-left', 'assistant-removed', 'assistant-added',
]);

export function isLifecycleMessage(m: Pick<ChatMessage, 'sourceTag'> | null | undefined): boolean {
  return !!m && m.sourceTag === LIFECYCLE_SOURCE_TAG;
}

/** The facts off a revived lifecycle doc; null when the row is not one (or malformed). */
export function lifecycleFacts(m: ChatMessage | null | undefined): ThreadLifecycleFacts | null {
  if (!isLifecycleMessage(m)) return null;
  const raw = m as unknown as Record<string, unknown>;
  const type = trim(raw.lifecycleType);
  if (!KNOWN_TYPES.has(type)) return null;
  const detailRaw = (raw.detail && typeof raw.detail === 'object' ? raw.detail : {}) as Record<string, unknown>;
  return {
    type: type as ThreadLifecycleType,
    actorId: trim(raw.actorId),
    actorName: trim(raw.actorName),
    detail: {
      ...(trim(detailRaw.title) ? { title: trim(detailRaw.title) } : {}),
      ...(trim(detailRaw.previousTitle) ? { previousTitle: trim(detailRaw.previousTitle) } : {}),
      ...(trim(detailRaw.targetId) ? { targetId: trim(detailRaw.targetId) } : {}),
      ...(trim(detailRaw.targetName) ? { targetName: trim(detailRaw.targetName) } : {}),
    },
  };
}

export interface LifecycleLineArgs {
  facts: ThreadLifecycleFacts;
  /** The viewer (renders "you"). */
  selfUid?: string | null;
  /** uid → the name the viewer knows them by (beats the server's stamp). */
  labelFor?: (uid: string) => string | null | undefined;
  lang?: 'en' | 'es';
}

/**
 * The row's sentence in the viewer's language. Names come from the viewer's
 * own roster first (a fresher rename wins), then the server's stamp, then a
 * neutral word — never a uid.
 */
export function lifecycleLine(args: LifecycleLineArgs): string {
  const { facts } = args;
  const self = trim(args.selfUid);
  const es = args.lang === 'es';
  const actorIsSelf = !!self && facts.actorId === self;
  const nameOf = (uid: string, stamped: string, neutral: string): string => {
    const known = uid ? trim(args.labelFor?.(uid)) : '';
    return known || trim(stamped) || neutral;
  };
  const actor = actorIsSelf ? (es ? 'Tú' : 'You') : nameOf(facts.actorId, facts.actorName, es ? 'Alguien' : 'Someone');
  const targetIsSelf = !!self && facts.detail.targetId === self;
  const target = nameOf(facts.detail.targetId ?? '', facts.detail.targetName ?? '', es ? 'un compañero' : 'a teammate');
  const title = trim(facts.detail.title);
  const previous = trim(facts.detail.previousTitle);
  const renamedFrom = previous && previous !== title;
  // Spanish conjugates for the viewer ("Cambiaste…"); English's "You" takes
  // the same verb form as a name, so one template serves both there.
  switch (facts.type) {
    case 'renamed':
      if (es) {
        if (actorIsSelf) {
          return renamedFrom
            ? `Cambiaste el nombre del hilo de ${quote(previous)} a ${quote(title)}`
            : `Le pusiste al hilo el nombre ${quote(title)}`;
        }
        return renamedFrom
          ? `${actor} cambió el nombre del hilo de ${quote(previous)} a ${quote(title)}`
          : `${actor} le puso al hilo el nombre ${quote(title)}`;
      }
      return renamedFrom
        ? `${actor} renamed the thread from ${quote(previous)} to ${quote(title)}`
        : `${actor} named the thread ${quote(title)}`;
    case 'member-added':
      if (targetIsSelf) return es ? `${actor} te añadió a este hilo` : `${actor} added you to this thread`;
      if (es) return actorIsSelf ? `Añadiste a ${target}` : `${actor} añadió a ${target}`;
      return `${actor} added ${target}`;
    case 'member-removed':
      if (es) return actorIsSelf ? `Quitaste a ${target} del hilo` : `${actor} quitó a ${target} del hilo`;
      return `${actor} removed ${target}`;
    case 'member-left':
      if (es) return actorIsSelf ? 'Saliste del hilo' : `${actor} salió del hilo`;
      return `${actor} left the thread`;
    case 'assistant-removed':
      if (es) return actorIsSelf ? 'Quitaste a Prest0n del hilo' : `${actor} quitó a Prest0n del hilo`;
      return `${actor} removed Prest0n from the thread`;
    case 'assistant-added':
      if (es) return actorIsSelf ? 'Volviste a añadir a Prest0n' : `${actor} volvió a añadir a Prest0n`;
      return `${actor} added Prest0n back`;
    default:
      if (es) return actorIsSelf ? 'Cambiaste el hilo' : `${actor} cambió el hilo`;
      return `${actor} changed the thread`;
  }
}
